/**
 * Property detail · Bank tab.
 *
 * Read-only display of the effective bank account, with an inheritance
 * badge that says where the value came from. Editing the property-level
 * override is reserved for a follow-up PR.
 */
import { Card } from '@/components/ui/card'
import { InheritanceBadge } from '@/components/admin/v2/InheritanceBadge'
import { useLanguage } from '@/contexts/LanguageContext'
import type { EffectiveBank } from '@/types/property'

type Props = {
  effectiveBank: EffectiveBank
}

export function BankTab({ effectiveBank }: Props) {
  const { t } = useLanguage()
  return (
    <Card className="p-6 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-0.5">
          <h2 className="text-base font-semibold">
            {t('admin.section.effectiveBankAccount', 'Effective bank account')}
          </h2>
          <p className="text-xs text-muted-foreground">
            Where rent for this property is sent. Resolved from room override → property
            override → lessor default.
          </p>
        </div>
        <InheritanceBadge source={effectiveBank.source} level="property" />
      </header>

      {effectiveBank.iban ? (
        <dl className="grid gap-3 sm:grid-cols-2 text-sm">
          <Row label={t('admin.field.bankHolder', 'Account holder')} value={effectiveBank.holder ?? '—'} />
          <Row label={t('admin.field.bankName', 'Bank name')} value={effectiveBank.bank_name ?? '—'} />
          <Row label={t('admin.field.bankIban', 'IBAN')} value={effectiveBank.iban} mono />
          <Row label={t('admin.field.bankBic', 'BIC')} value={effectiveBank.bic ?? '—'} mono />
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t(
            'admin.field.noBankConfigured',
            'No bank account configured. Set defaults on the linked lessor or add a property-level override.',
          )}
        </p>
      )}
    </Card>
  )
}

function Row({
  label,
  value,
  mono,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`font-medium ${mono ? 'font-mono text-sm' : ''}`}>{value}</dd>
    </div>
  )
}
